import { Image, Text } from '@user-portal/wikr-components';
import { Trans, useTranslation } from 'react-i18next';

import { generatePlanRequest, resetWorkout, setCompletePlanModalVisibility } from 'store/personalPlan/actions';
import { selectCurrentPlanLevel, selectTotalPlanDays } from 'store/personalPlan/selectors';
import { selectCurrentUser } from 'store/user/selectors';

import {
    sendAnalyticIncreaseLevelButtonClick,
    sendAnalyticKeepCurrentLevelButtonClick,
} from 'services/Analytics/trackers/completePersonalPlan';

import { useDispatch, useSelector } from 'hooks';

import isMobile from 'helpers/isMobile';
import { isTabletSize } from 'helpers/utils';
import { increaseComplexityLevel } from 'helpers/workout';

import Cross from 'components/Icon/Cross';

import WorkoutProgressBoard from '../../components/WorkoutProgressBoard';

import check from './assets/img/check.png';
import completePlanDesktopImg from './assets/img/complete_plan_desktop.png';
import completePlanMobileImg from './assets/img/complete_plan_mobile.png';
import completePlanTabletImg from './assets/img/complete_plan_tablet.png';
import lotus from './assets/img/lotus.png';

import * as S from './styled';

const CompletePersonalPlanModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentLevel = useSelector(selectCurrentPlanLevel);
    const { first_name } = useSelector(selectCurrentUser);
    const { newLevel, isPossibleToIncreaseLevel } = increaseComplexityLevel(currentLevel);

    const totalPlanDays = useSelector(selectTotalPlanDays);

    const imgUrl = () => {
        if (isTabletSize()) return completePlanTabletImg;

        if (isMobile.any()) return completePlanMobileImg;

        return completePlanDesktopImg;
    };

    const onClose = () => dispatch(setCompletePlanModalVisibility(false));

    const handleIncreaseLevel = () => {
        onClose();

        sendAnalyticIncreaseLevelButtonClick();
        dispatch(generatePlanRequest({ level: newLevel }));
        dispatch(resetWorkout());
    };

    const handleKeepLevel = () => {
        onClose();

        sendAnalyticKeepCurrentLevelButtonClick();
        dispatch(generatePlanRequest({ level: currentLevel }));
        dispatch(resetWorkout());
    };

    return (
        <S.Container>
            <S.CloseBtn onClick={onClose} data-testid="close-btn">
                <Cross />
            </S.CloseBtn>

            <S.TopWrapper>
                <S.TopWrapperContent>
                    <S.TopWrapperRightCol center maxWidth={450} mb={20} alt="Complete plan" src={imgUrl()} />
                    <S.TopWrapperLeftCol>
                        <S.TopWrapperSubtitle>
                            <S.TopWrapperSubtitleIcon maxWidth={20} alt="Complete icon" src={check} />
                            <div data-locator="durationCompletedPlan" data-testid="durationCompletedPlan">
                                <S.TopWrapperSubtitleText medium color="text-secondary">
                                    {t('personalPlan.completePlanModal.subtitle', { days: totalPlanDays })}
                                </S.TopWrapperSubtitleText>
                            </div>
                        </S.TopWrapperSubtitle>
                        <S.TopWrapperLeftColText type="h4" medium mb={24}>
                            {t('personalPlan.completePlanModal.title')}
                        </S.TopWrapperLeftColText>
                        <WorkoutProgressBoard />
                    </S.TopWrapperLeftCol>
                </S.TopWrapperContent>
            </S.TopWrapper>

            <S.BottomWrapper>
                <S.BottomWrapperHeader>
                    <span role="img" aria-label="fire">
                        🔥
                    </span>
                    <Text type="overline">{t('personalPlan.completePlanModal.youAreBetterThan')}</Text>
                </S.BottomWrapperHeader>
                <S.BottomWrapperBody>
                    <S.BottomWrapperBodyCol>
                        <S.BottomWrapperBodyTitle>
                            <Image maxWidth={24} alt="Complete plan" src={lotus} />
                            <div data-locator="wellDoneMessage" data-testid="wellDoneMessage">
                                <Text medium type="large-text">
                                    {t('personalPlan.completePlanModal.wellDone.title', { name: first_name })}
                                </Text>
                            </div>
                        </S.BottomWrapperBodyTitle>
                        <Text mb={20} color="text-secondary" type="small-text">
                            <Trans
                                i18nKey="personalPlan.completePlanModal.wellDone.description"
                                components={{ 1: <br /> }}
                            />
                        </Text>
                    </S.BottomWrapperBodyCol>
                    <S.BottomWrapperBodyCol>
                        {isPossibleToIncreaseLevel && (
                            <S.BottomWrapperIncreaseLeaveBtn
                                dataLocator="IncreaseLvlBtn"
                                dataTestId="IncreaseLvlBtn"
                                onClick={handleIncreaseLevel}
                                mb={12}
                            >
                                {t('personalPlan.completePlanModal.wellDone.increaseLevelBtn')}
                            </S.BottomWrapperIncreaseLeaveBtn>
                        )}
                        <S.BottomWrapperKeepLevelBtn onClick={handleKeepLevel} dataLocator="keppCurrentLvlBtn" mb={20}>
                            {t('personalPlan.completePlanModal.wellDone.keepLevelBtn')}
                        </S.BottomWrapperKeepLevelBtn>
                    </S.BottomWrapperBodyCol>
                </S.BottomWrapperBody>
            </S.BottomWrapper>
        </S.Container>
    );
};

export default CompletePersonalPlanModal;
